import React from 'react'
import { Helmet } from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import { Article, BreadcrumbList } from 'schema-dts'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'

import { Post, PostResumeSample } from '../../types/post'
import { TwitterSEO } from './OpenGraphSEO'
import useBrand from '../../hooks/useBrand'
import useUrlPathBuilder from '../../hooks/useUrlPathBuilder'
import { getImageURLsFromReferences } from '../RichText/utils'
import { insertHandlebarsTemplate } from '../../utils/insertHandlebarsTemplate'
import useHandlebarsContext from '../../hooks/useHandlebarsContext'
import NoIndex from './NoIndex'
import useSiteData from '../../hooks/useSiteData'

interface Props {
  post: Post | PostResumeSample
  shouldRenderNoIndex: boolean
}

const PostSEO: React.FC<Props> = ({ post, shouldRenderNoIndex }) => {
  // For branding purposes, we want to use the parent site's brand. This should
  // always set, but in the case it is not, use the brand from the post.
  const siteBrand = useBrand()
  const site = useSiteData()
  const { urlPathBuilder, baseURL, blogPrefix } = useUrlPathBuilder()
  const brand = siteBrand ?? post.brand?.[0] ?? {}
  const handlebarsContext = useHandlebarsContext()
  const description = insertHandlebarsTemplate(
    post.seo?.description ?? post.seo?.openGraphDescription,
    handlebarsContext
  )
  const plainTextPost = documentToPlainTextString(JSON.parse(post.body.raw))
  const imageURLs = getImageURLsFromReferences(post.body.references ?? [])
  let openGraphImage: string | undefined = undefined
  let openGraphImageAlt: string | undefined = undefined
  let openGraphDescription: string | undefined = undefined
  let featuredImage: string | undefined = undefined
  const canonicalBaseURL = brand?.productionUrl
  const canonicalBlogPrefix = brand.blogPrefix ?? blogPrefix

  const defaultCanonicalURL =
    post.seo?.canonicalUrl ??
    urlPathBuilder([canonicalBlogPrefix, post.slug], {
      baseURL: canonicalBaseURL,
    })
  const pageURL = urlPathBuilder(
    [siteBrand.blogPrefix ?? blogPrefix, post.slug],
    {
      baseURL: siteBrand.productionUrl,
    }
  )

  if (post.__typename === 'ContentfulPost' && post.featuredImage?.file) {
    featuredImage = post.featuredImage.file.url
    openGraphImageAlt = post.featuredImage.description
  }

  if (post.seo?.openGraphImage) {
    openGraphImage = post.seo.openGraphImage.file.url
    openGraphImageAlt = post.seo.openGraphImage.description
  } else if (featuredImage) {
    openGraphImage = featuredImage
  }

  if (post.seo?.openGraphDescription) {
    openGraphDescription = insertHandlebarsTemplate(
      post.seo.openGraphDescription,
      handlebarsContext
    )
  } else if (post.seo?.description) {
    openGraphDescription = insertHandlebarsTemplate(
      post.seo.description,
      handlebarsContext
    )
  }

  if (openGraphImage && openGraphImage.startsWith('//')) {
    openGraphImage = `https:${openGraphImage}`
  }

  const date = post.publishedAt ? post.publishedAt : post.date

  return <>
    <Helmet
      script={[
        helmetJsonLdProp<Article>({
          '@context': 'https://schema.org',
          '@type': 'Article',
          name: post.title,
          headline: post.title,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt,
          image: [featuredImage, ...imageURLs].filter((url): url is string =>
            Boolean(url)
          ),
          educationalUse: 'Job Search',
          isAccessibleForFree: true,
          isFamilyFriendly: true,
          mainEntityOfPage: urlPathBuilder([blogPrefix], {
            baseURL: brand.productionUrl,
          }),
          articleBody: plainTextPost,
          wordCount: plainTextPost.split(/\s+/).length,
          timeRequired: `PT${Math.round(post.fields.readingTime.minutes)}m`,
          author: post.authors?.length
            ? post.authors.map((author) => ({
                '@type': 'Person',
                name: author.name,
                image: author.picture?.file.url ?? undefined,
              }))
            : undefined,
          publisher: {
            '@id': urlPathBuilder([], {
              baseURL,
              hash: 'organization',
            }),
          },
          copyrightHolder: {
            '@id': urlPathBuilder([], {
              baseURL,
              hash: 'organization',
            }),
          },
        }),
        helmetJsonLdProp<BreadcrumbList>({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@type': 'WebPage',
                '@id': urlPathBuilder([], { baseURL }),
                url: urlPathBuilder([], { baseURL }),
                name: `${brand.name} Home Page`,
              },
            },
            {
              '@type': 'ListItem',
              position: 2,
              item: {
                '@type': 'WebPage',
                '@id': urlPathBuilder([blogPrefix], { baseURL }),
                url: urlPathBuilder([blogPrefix], { baseURL }),
                name: `${brand.name} Blog`,
              },
            },
            {
              '@type': 'ListItem',
              position: 3,
              item: {
                '@type': 'WebPage',
                '@id': urlPathBuilder([blogPrefix, post.slug], { baseURL }),
                url: urlPathBuilder([blogPrefix, post.slug], { baseURL }),
                name: post.title,
              },
            },
          ],
        }),
      ]}
    >
      <title>{post.seo?.pageTitle ?? post.title}</title>
      {description && <meta name="description" content={description} />}
      {post.authors?.map(({ name }) => (
        <meta name="author" content={name} key={name} />
      ))}
      {/* Canonical links. Do not use the site brand here and always use the
        brand in the SEO, because this must be unique for this post regardless
        of brand. */}
      <link rel="canonical" href={defaultCanonicalURL} />
      {/* When we have the sites in multiple languages, determine how to do alternates */}
      {/* OpenGraph */}
      <meta property="og:site_name" content={brand.name} />
      <meta property="article:publisher" content={brand.facebook} />
      <meta property="og:type" content="article" />
      <meta name="article:published_time" content={date} />
      <meta name="article:modified_time" content={post.updatedAt} />
      {post.categories?.map(({ name }) => (
        <meta name="article:tag" content={name} key={name} />
      ))}
      <meta
        property="og:title"
        content={post.seo?.openGraphTitle ?? post.title}
      />
      <meta property="og:url" content={pageURL} />
      {openGraphDescription && (
        <meta property="og:description" content={openGraphDescription} />
      )}
      {openGraphImage && (
        <meta property="og:image" content={openGraphImage} />
      )}
    </Helmet>
      {/* Twitter OpenGraph */}
      <TwitterSEO
      title={post.title}
      imageURL={openGraphImage}
      imageAlt={openGraphImageAlt}
      description={description}
      data={{
        'Written by':
          post.authors?.map((a) => a.name).join(', ') ?? `${brand.name} Team`,
        'Est. reading time': `${Math.ceil(
          post.fields.readingTime.minutes
        )} minutes`,
      }}
    />
    {shouldRenderNoIndex && <NoIndex seo={post.seo} slug={post.slug} />}
  </>;
}

// export default React.memo(PostSEO, isEqual)
export default PostSEO
